import { useState, type ChangeEvent, type DragEvent, type FC } from "react";
import UploadZone from "./UploadZone";
import UploadedFile from "./UploadedFile";

export interface FileUploadProps {
  value?: File | string | null;
  onChange: (file?: File | null) => void;
  accept: string;
  isError?: boolean;
  dataTest?: string;
}

export const UPLOADING_TIMEOUT_MS = 1000;

export const FileUpload: FC<FileUploadProps> = ({ value, onChange, accept, isError, dataTest }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const prevent = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    prevent(e);
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    startUpload(file);
  };

  const startUpload = (file: File) => {
    onChange(file);
    setUploading(true);

    setTimeout(() => setUploading(false), UPLOADING_TIMEOUT_MS);
  };

  const handleRemove = () => {
    onChange(null);
    setUploading(false);
  };

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) startUpload(file);
  };

  return (
    <div className="w-full" data-test={dataTest}>
      {value ? (
        <UploadedFile
          isError={isError}
          file={value}
          uploading={uploading}
          onRemove={handleRemove}
          dataTest={dataTest}
        />
      ) : (
        <UploadZone
          isError={isError}
          accept={accept}
          dragActive={dragActive}
          onSelect={handleSelect}
          onDragEnter={(e: DragEvent<HTMLLabelElement>) => {
            prevent(e);
            setDragActive(true);
          }}
          onDragLeave={(e: DragEvent<HTMLLabelElement>) => {
            prevent(e);
            setDragActive(false);
          }}
          onDragOver={prevent}
          onDrop={handleDrop}
          dataTest={dataTest}
        />
      )}
    </div>
  );
};
